import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

let razorpayScriptPromise = null;
function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (!razorpayScriptPromise) {
    razorpayScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.async = true;
      s.onload = () => resolve(true);
      s.onerror = () => {
        razorpayScriptPromise = null;
        reject(new Error("Could not load Razorpay Checkout"));
      };
      document.body.appendChild(s);
    });
  }
  return razorpayScriptPromise;
}

/**
 * RazorpayCheckoutButton
 * Props:
 *  - amount (number, in rupees) — required
 *  - purpose (string) — optional label ("Premium Monthly", "10-Chat Credit Pack", etc.)
 *  - description (string) — modal subtitle
 *  - buttonText (string) — override label
 *  - prefill { name, email, contact } — optional
 *  - onSuccess(payment) — fired after signature verified server-side
 *  - onFailure(err) — fired on dismiss / gateway error
 */
export default function RazorpayCheckoutButton({
  amount,
  purpose = "",
  description = "InsureGenie payment",
  buttonText,
  prefill = {},
  onSuccess,
  onFailure,
  className = "",
  disabled = false,
}) {
  const [scriptReady, setScriptReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadRazorpayScript()
      .then(() => mounted && setScriptReady(true))
      .catch(() => mounted && setScriptReady(false));
    return () => { mounted = false; };
  }, []);

  const startCheckout = useCallback(async () => {
    if (busy || !scriptReady) return;
    setBusy(true);
    try {
      // 1. Ask backend to create an order — never trust client-side amount for signature
      const { data } = await api.post("/payments/create-order", { amount, purpose });
      const { order_id, amount: paise, currency, key_id } = data;

      // 2. Open Razorpay Standard Checkout modal
      const rzp = new window.Razorpay({
        key: key_id,
        amount: paise,
        currency,
        name: "InsureGenie",
        description,
        order_id,
        prefill,
        theme: { color: "#2563EB" },
        modal: {
          ondismiss: () => {
            setBusy(false);
            toast.message("Checkout closed", { description: "No payment was made." });
            onFailure?.(new Error("Checkout dismissed by user"));
          },
        },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful", { description: `₹${amount} confirmed & verified.` });
            onSuccess?.(resp);
          } catch (e) {
            const msg = formatApiError(e) || "Payment verification failed";
            toast.error("Payment recorded but not verified", { description: msg });
            onFailure?.(new Error(msg));
          } finally {
            setBusy(false);
          }
        },
      });

      rzp.on("payment.failed", (resp) => {
        setBusy(false);
        const desc = resp?.error?.description || "Payment failed";
        toast.error("Payment failed", { description: desc });
        onFailure?.(resp?.error || new Error(desc));
      });

      rzp.open();
    } catch (e) {
      setBusy(false);
      const msg = formatApiError(e) || "Could not start checkout";
      toast.error("Could not start checkout", { description: msg });
      onFailure?.(new Error(msg));
    }
  }, [amount, purpose, description, prefill, scriptReady, busy, onSuccess, onFailure]);

  return (
    <Button
      data-testid="razorpay-checkout-btn"
      onClick={startCheckout}
      disabled={disabled || busy || !scriptReady || !amount || amount <= 0}
      className={`gap-2 ${className}`}
    >
      {busy ? (
        <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
      ) : (
        <>
          <CreditCard className="w-4 h-4" />
          {buttonText || `Pay ₹${amount}`}
          <ShieldCheck className="w-3.5 h-3.5 opacity-70 ml-1" />
        </>
      )}
    </Button>
  );
}
